import React, { useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
  Filler, Title, Tooltip, Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Box, IconButton, Tooltip as MuiTooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  Filler, Title, Tooltip, Legend, ChartDataLabels,
);

// Shorter labels for better display
const allLabels = ['Hasta 4 MDP', '4-100 MDP', '100-250 MDP', '250-1,000 MDP', '1,000-10,000 MDP', 'Más de 10,000 MDP'];

// Adoption of carbon-footprint measurement by annual billing size (% of firms that measure)
// Categories: [Hasta 4, 4-100, 100-250, 250-1,000, 1,000-10,000, Más de 10,000] MDP
const data2023 = [25.0, 0.0, 12.5, 25.0, 0.0, 37.5];
const data2024 = [15.8, 5.3, 5.3, 21.1, 26.3, 26.3];
const data2025 = [2.0, 5.0, 0.0, 0.0, 18.0, 75.0];


const options = {
  responsive: true,
  maintainAspectRatio: false,
  layout: {
    padding: {
      top: 30,
      bottom: 20,
      left: 10,
      right: 10,
    },
  },
  plugins: {
    legend: {
      position: 'top',
      labels: {
        font: { size: 12, weight: 'bold' },
        usePointStyle: true,
        pointStyle: 'circle',
        padding: 20,
      },
    },
    tooltip: {
      backgroundColor: 'rgba(31, 61, 88, 0.9)',
      titleFont: { size: 13, weight: 'bold' },
      bodyFont: { size: 12 },
      padding: 12,
      cornerRadius: 8,
      callbacks: {
        label: (context) => `${context.dataset.label}: ${context.raw}%`,
      },
    },
    datalabels: {
      color: '#000',
      font: { weight: 'bold', size: 10 },
      formatter: (value) => `${value.toFixed(1)}%`,
      anchor: 'end',
      align: 'top',
      offset: 6,
    },
  },
  scales: {
    x: {
      title: {
        display: true,
        text: 'Facturación Anual',
        font: { size: 12, weight: 'bold' },
        color: '#333',
      },
      ticks: {
        font: { size: 10 },
        maxRotation: 25,
        minRotation: 0,
        color: '#555',
      },
      grid: {
        display: false,
      },
    },
    y: {
      title: {
        display: true,
        text: 'Porcentaje (%)',
        font: { size: 12, weight: 'bold' },
        color: '#333',
      },
      beginAtZero: true,
      max: 80,
      ticks: {
        stepSize: 10,
        font: { size: 10 },
        color: '#555',
      },
      grid: {
        color: 'rgba(0, 0, 0, 0.08)',
      },
    },
  },
};

const HuellaFacturacion = ({ selectedCategories = allLabels }) => {
  const [infoOpen, setInfoOpen] = useState(false);

  const chartData = {
    labels: allLabels,
    datasets: [
      {
        label: '2023',
        data: data2023,
        backgroundColor: 'rgba(162, 174, 187, 0.15)',
        borderColor: '#A2AEBB',
        borderWidth: 3,
        pointBackgroundColor: '#A2AEBB',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
        fill: true,
        tension: 0.3,
      },
      {
        label: '2024',
        data: data2024,
        backgroundColor: 'rgba(71, 106, 142, 0.15)',
        borderColor: '#476A8E',
        borderWidth: 3,
        pointBackgroundColor: '#476A8E',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
        fill: true,
        tension: 0.3,
      },
      {
        label: '2025',
        data: data2025,
        backgroundColor: 'rgba(31, 61, 88, 0.15)',
        borderColor: '#1F3D58',
        borderWidth: 3,
        pointBackgroundColor: '#1F3D58',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
        fill: true,
        tension: 0.3,
      },
    ],
  };


  const infoContent = {
    formula: 'Porcentaje = (Empresas que miden huella de carbono en categoría / Total de empresas que miden) × 100',
    description: 'Muestra la cantidad de emisiones de gases de efecto invernadero que se emiten a la atmósfera por medio de alguna actividad humana, que puede ser un producto o un servicio.',
  };

  return (
    <div style={{ maxWidth: '800px', height: '500px', margin: '0 auto', padding: '20px' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={-5}>
        <MuiTooltip title="Información de la gráfica">
          <IconButton onClick={() => setInfoOpen(true)}>
            <InfoIcon />
          </IconButton>
        </MuiTooltip>
      </Box>
      <Line data={chartData} options={options} />
      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)}>
        <DialogTitle>Información de la Gráfica</DialogTitle>
        <DialogContent>
          <p><strong>Fórmula:</strong> {infoContent.formula}</p>
          <p><strong>Descripción:</strong> {infoContent.description}</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default HuellaFacturacion;